import { useState } from "react";
import { formatCurrency, formatCurrencyExact, SUB_CATEGORIES } from "../../lib/romEngine";
import GanttTimeline from "../../components/GanttTimeline";
import RateLibraryPicker from "../../components/RateLibraryPicker";
import { CollapsibleSection, Field, EditableNum, SectionToolbar, MiscRow, KV } from "./primitives";
import { addDaysISO, fmtDateShort } from "./helpers";
import { RoomScopeSummary } from "./RoomScopeSummary";

export function DeliverablesBlock({ result, inputs, schedule, setField, vendorStats = {} }) {
    const [libraryFor, setLibraryFor] = useState(null); // null | "labour" | "equipment"
    const usePrime = !!inputs.primeArea;
    const otMult = Number(schedule?.conditions?.ot_multiplier) || 1.5;
    const overrides = inputs.labour_overrides || {};
    const extras = inputs.labour_extras || [];

    const setOverride = (role, key, raw) => {
        const v = raw === "" ? undefined : Number(raw);
        const prev = overrides[role] || {};
        const next = { ...prev, [key]: v };
        const hasAny = Object.values(next).some((x) => x !== undefined);
        const nextAll = { ...overrides };
        if (hasAny) nextAll[role] = next; else delete nextAll[role];
        setField("labour_overrides", nextAll);
    };

    const setExtra = (idx, key, raw) => {
        const next = [...extras];
        next[idx] = { ...(next[idx] || {}), [key]: key === "role" ? raw : (raw === "" ? "" : Number(raw)) };
        setField("labour_extras", next);
    };
    const addExtra = () => setField("labour_extras", [...extras, { role: "", headcount: 1, hours: 8, rate: 0 }]);
    const delExtra = (idx) => setField("labour_extras", extras.filter((_, i) => i !== idx));
    const resetLabour = () => { setField("labour_overrides", {}); setField("labour_extras", []); };
    const labourDirty = Object.keys(overrides).length > 0 || extras.length > 0;
    const removeBaseLabour = (role) => setField("labour_overrides", { ...overrides, [role]: { ...(overrides[role] || {}), removed: true } });

    /* ---- B Consumables ---- */
    const setConsumablesPct = (v) => setField("consumablesPct", v);

    /* ---- C Equipment ---- */
    const eqOverrides = inputs.equipment_overrides || {};
    const eqExtras = inputs.equipment_extras || [];
    const setEqOverride = (item, key, raw) => {
        const v = raw === "" ? undefined : Number(raw);
        const prev = eqOverrides[item] || {};
        const next = { ...prev, [key]: v };
        const hasAny = Object.values(next).some((x) => x !== undefined);
        const nextAll = { ...eqOverrides };
        if (hasAny) nextAll[item] = next; else delete nextAll[item];
        setField("equipment_overrides", nextAll);
    };
    const setEqExtra = (idx, key, raw) => {
        const next = [...eqExtras];
        next[idx] = { ...(next[idx] || {}), [key]: key === "item" ? raw : (raw === "" ? "" : Number(raw)) };
        setField("equipment_extras", next);
    };
    const addEqExtra = () => setField("equipment_extras", [...eqExtras, { item: "", qty: 1, days: 1, rate: 0 }]);
    const delEqExtra = (idx) => setField("equipment_extras", eqExtras.filter((_, i) => i !== idx));
    const resetEquip = () => { setField("equipment_overrides", {}); setField("equipment_extras", []); };
    const eqDirty = Object.keys(eqOverrides).length > 0 || eqExtras.length > 0;
    const removeBaseEquip = (item) => setField("equipment_overrides", { ...eqOverrides, [item]: { ...(eqOverrides[item] || {}), removed: true } });

    /* ---- D Subcontractors ---- */
    const subs = inputs.subcontractors || [];
    const setSub = (idx, key, raw) => {
        const next = [...subs];
        next[idx] = { ...(next[idx] || {}), [key]: (key === "item" || key === "category") ? raw : (raw === "" ? "" : Number(raw)) };
        setField("subcontractors", next);
    };
    const addSub = () => setField("subcontractors", [...subs, { item: "", category: "trade", qty: 1, days: 1, rate: 0, markupPct: 0.20 }]);
    const delSub = (idx) => setField("subcontractors", subs.filter((_, i) => i !== idx));

    /* ---- E Misc / Incidentals ---- */
    const miscOv = inputs.misc_overrides || {};
    const miscExtras = inputs.misc_extras || [];
    const setMiscOv = (key, raw) => {
        const v = raw === "" ? undefined : Number(raw);
        const next = { ...miscOv };
        if (v === undefined) delete next[key]; else next[key] = v;
        setField("misc_overrides", next);
    };
    const setMiscExtra = (idx, key, raw) => {
        const next = [...miscExtras];
        next[idx] = { ...(next[idx] || {}), [key]: key === "label" ? raw : (raw === "" ? "" : Number(raw)) };
        setField("misc_extras", next);
    };
    const addMiscExtra = () => setField("misc_extras", [...miscExtras, { label: "", qty: 1, rate: 0, days: 1 }]);
    const delMiscExtra = (idx) => setField("misc_extras", miscExtras.filter((_, i) => i !== idx));
    const resetMisc = () => { setField("misc_overrides", {}); setField("misc_extras", []); };
    const miscDirty = Object.keys(miscOv).length > 0 || miscExtras.length > 0;

    /* ---- Timeline overrides ---- */
    const timelineOv = inputs.timeline_overrides || {};
    const timelineExtras = inputs.timeline_extras || [];
    const onPhaseChange = (id, patch) => {
        const prev = timelineOv[id] || {};
        setField("timeline_overrides", { ...timelineOv, [id]: { ...prev, ...patch } });
    };
    const onPhaseExtraChange = (id, patch) => {
        if (patch === null) {
            setField("timeline_extras", timelineExtras.filter((p) => p.id !== id));
        } else {
            setField("timeline_extras", timelineExtras.map((p) => p.id === id ? { ...p, ...patch } : p));
        }
    };
    const onAddPhase = () => {
        const id = `extra-${Date.now()}`;
        setField("timeline_extras", [...timelineExtras, { id, phase: "New phase", start: result.days + 1, days: 2, tasks: [] }]);
    };
    const resetTimeline = () => { setField("timeline_overrides", {}); setField("timeline_extras", []); };
    const timelineDirty = Object.keys(timelineOv).length > 0 || timelineExtras.length > 0;

    /* ---- B incidentals default labels ---- */
    const bi = result.breakdown_inputs;
    // Section B chip — the base % the estimator set vs the scope-adjusted rate
    // actually applied to labour (consumables_scope_factor, e.g. full_gut = 2.6x).
    const consumablesBasePct = Number(((Number(inputs.consumablesPct) || 0) * 100).toFixed(1));
    const consumablesScopeFactor = Number(bi.consumables_scope_factor ?? 1);
    const consumablesEffectivePct = Number((consumablesBasePct * consumablesScopeFactor).toFixed(1));
    const consumablesChipLabel = consumablesScopeFactor !== 1
        ? `B: Consumables (${consumablesBasePct}% \u2192 scope-adjusted to ${consumablesEffectivePct}%)`
        : `B: Consumables (${consumablesBasePct}%)`;

    /* ---- Rate library picker ---- */
    const libraryItems = libraryFor === "labour"
        ? (schedule?.schedule_a || []).map((r) => {
            const rate = usePrime ? r.prime : r.standard;
            const inSeeded = result.labour_lines.find((l) => !l.auto && !l.custom && l.role === r.role);
            const inExtras = extras.findIndex((x) => (x.role || "").trim() === r.role);
            return {
                key: r.role,
                label: r.role,
                rate,
                rateLabel: "per hr",
                category: usePrime ? "Schedule A · Prime rates" : "Schedule A · Standard rates",
                alreadyInTable: !!inSeeded || inExtras >= 0,
            };
        })
        : libraryFor === "equipment"
            ? (schedule?.schedule_c || []).map((r) => {
                const inSeeded = result.equipment_lines.find((l) => !l.custom && l.item === r.item);
                const inExtras = eqExtras.findIndex((x) => (x.item || "").trim() === r.item);
                return {
                    key: r.item,
                    label: r.item,
                    rate: r.rate_per_day,
                    rateLabel: "per day",
                    category: r.category || "Other",
                    alreadyInTable: !!inSeeded || inExtras >= 0,
                };
            })
            : [];

    const subLibraryItems = libraryFor === "subs"
        ? (schedule?.schedule_d || []).map((r) => {
            const catLabel = (SUB_CATEGORIES.find((c) => c.v === r.category) || {}).label || "Other";
            const st = vendorStats[r.item];
            const badge = st && st.variance_pct != null
                ? { text: `${st.variance_pct > 0 ? "+" : ""}${st.variance_pct}% · ${st.jobs}job${st.jobs === 1 ? "" : "s"}`, tone: st.direction }
                : null;
            return {
                key: r.item,
                label: r.vendor ? `${r.item} · ${r.vendor}` : r.item,
                rate: r.rate,
                rateLabel: r.unit || "",
                category: catLabel,
                badge,
                _row: r,
            };
        })
        : [];
    const pickerItems = libraryFor === "subs" ? subLibraryItems : libraryItems;

    function onPickLibrary(it) {
        if (libraryFor === "subs") {
            const r = it._row || {};
            const idx = subs.findIndex((s) => (s.item || "").trim() === (r.item || it.key));
            if (idx >= 0) {
                setSub(idx, "qty", (Number(subs[idx].qty) || 0) + 1);
                return;
            }
            setField("subcontractors", [...subs, {
                item: r.item || it.key,
                category: r.category || "trade",
                qty: 1,
                days: 1,
                rate: r.rate ?? it.rate,
                markupPct: r.default_markup_pct ?? 0.20,
            }]);
            return;
        }
        if (libraryFor === "labour") {
            const seeded = result.labour_lines.find((l) => !l.auto && !l.custom && l.role === it.key);
            if (seeded) {
                const cur = (overrides[seeded.role] || {}).headcount ?? seeded.headcount;
                setOverride(seeded.role, "headcount", (Number(cur) || 0) + 1);
                return;
            }
            const idx = extras.findIndex((x) => (x.role || "").trim() === it.key);
            if (idx >= 0) {
                setExtra(idx, "headcount", (Number(extras[idx].headcount) || 0) + 1);
                return;
            }
            setField("labour_extras", [...extras, { role: it.key, headcount: 1, hours: 8, rate: it.rate }]);
        } else if (libraryFor === "equipment") {
            const seeded = result.equipment_lines.find((l) => !l.custom && l.item === it.key);
            if (seeded) {
                const cur = (eqOverrides[seeded.item] || {}).qty ?? seeded.qty;
                setEqOverride(seeded.item, "qty", (Number(cur) || 0) + 1);
                return;
            }
            const idx = eqExtras.findIndex((x) => (x.item || "").trim() === it.key);
            if (idx >= 0) {
                setEqExtra(idx, "qty", (Number(eqExtras[idx].qty) || 0) + 1);
                return;
            }
            setField("equipment_extras", [...eqExtras, { item: it.key, qty: 1, days: 1, rate: it.rate }]);
        }
    }

    return (
        <>
            <RoomScopeSummary rooms={inputs.rooms} siteConditions={inputs.siteConditions} />
            <CollapsibleSection tag="A" summary={formatCurrency(result.labour_total)} title={`Labour force composition · ${inputs.lossType.toUpperCase()} · ${result.crew_size} crew · ${result.days} days`}>
                <SectionToolbar dirty={labourDirty} onReset={resetLabour}>
                    Click any cell to override the engine&apos;s suggestion — cells accept <b>= formulas</b> (e.g. 4*8). Subtotals recalculate automatically.
                </SectionToolbar>
                <table className="rom-table rom-table--responsive" data-testid="labour-table">
                    <thead>
                        <tr>
                            <th>Role</th>
                            <th className="numeric">Crew</th>
                            <th className="numeric">Hours</th>
                            <th className="numeric">Rate/hr</th>
                            <th className="numeric">Subtotal</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {result.labour_lines.filter((l) => !l.custom).map((l, i) => {
                            const ov = overrides[l.role] || {};
                            const lineIsAuto = !!l.auto;
                            if (l.supervision) {
                                const compTip = (l.composition || [])
                                    .map((d) => `${d.role} ${Math.round((d.weight || 0) * 100)}% @ $${d.rate}/hr`)
                                    .join("  ·  ") + `  →  blended $${l.rate}/hr`;
                                return (
                                    <tr key={`r-${i}`} data-testid="labour-row-supervision" className="bg-slate-50/60">
                                        <td className="font-medium card-title">
                                            <span title={compTip} className="border-b border-dotted border-[var(--rom-text-3)] cursor-help" data-testid="supervision-composition-tip">
                                                Project Management / Supervision
                                            </span>
                                            <span className="ml-2 chip chip-navy" style={{ padding: "1px 5px" }}>% of field labour</span>
                                            <div className="text-[10px] text-[var(--rom-text-3)] uppercase tracking-wider mt-0.5">Blended PM-tier · hover for make-up</div>
                                        </td>
                                        <td className="numeric" data-label="% of field labour">
                                            <div className="flex items-center justify-end gap-1">
                                                <EditableNum
                                                    step="0.1"
                                                    value={Number((l.pct * 100).toFixed(2))}
                                                    placeholder={String((l.pct * 100).toFixed(1))}
                                                    onChange={(v) => setField("supervision_pct_override", v === "" || v == null ? undefined : Number(v) / 100)}
                                                    testId="supervision-pct"
                                                />
                                                <span className="text-[10px] text-[var(--rom-text-3)]">%</span>
                                            </div>
                                        </td>
                                        <td className="numeric text-[var(--rom-text-2)]" data-label="Hours">{l.hours}</td>
                                        <td className="numeric text-[var(--rom-text-2)]" data-label="Rate/hr">{formatCurrencyExact(l.rate)}</td>
                                        <td className="numeric font-semibold" data-label="Subtotal">{formatCurrencyExact(l.subtotal)}</td>
                                        <td></td>
                                    </tr>
                                );
                            }
                            return (
                                <tr key={`r-${i}`} data-testid={`labour-row-${l.role}`}>
                                    <td className="font-medium card-title">
                                        {l.role}
                                        {l.overridden && <span className="ml-2 chip chip-orange" style={{padding: "1px 5px"}}>edited</span>}
                                        {l.infeasible && (
                                            <div className="text-[10px] mt-1 inline-flex items-center gap-1 text-[var(--rom-danger)] bg-red-50 border border-red-200 rounded px-1.5 py-0.5" data-testid={`labour-infeasible-${l.role}`}>
                                                ⚠ Infeasible at this headcount — extend duration or add headcount{l.ot_per_day_per_person ? ` (~${l.ot_per_day_per_person} OT hrs/day/person)` : ""}
                                            </div>
                                        )}
                                        {l.ot_premium > 0.005 && (
                                            <div className="text-[10px] mt-1 inline-flex items-center gap-1 text-[var(--rom-orange)] bg-[var(--rom-orange-soft)]/50 border border-[var(--rom-orange)]/30 rounded px-1.5 py-0.5" data-testid={`labour-ot-premium-${l.role}`}>
                                                + {formatCurrencyExact(l.ot_premium)} OT premium ({l.ot_hours} hrs @ {otMult}×) — subtotal = hours × rate + OT premium
                                            </div>
                                        )}
                                    </td>
                                    <td className="numeric" data-label="Crew">
                                        {lineIsAuto ? "—" : (
                                            <EditableNum
                                                value={ov.headcount ?? l.headcount}
                                                placeholder={String(l.headcount)}
                                                onChange={(v) => setOverride(l.role, "headcount", v)}
                                                testId={`labour-crew-${l.role}`}
                                            />
                                        )}
                                    </td>
                                    <td className="numeric" data-label="Hours">
                                        {lineIsAuto ? "—" : (
                                            <EditableNum
                                                step="0.1"
                                                value={ov.hours ?? l.hours}
                                                placeholder={String(l.hours)}
                                                onChange={(v) => setOverride(l.role, "hours", v)}
                                                testId={`labour-hours-${l.role}`}
                                            />
                                        )}
                                    </td>
                                    <td className="numeric" data-label="Rate/hr">
                                        {lineIsAuto ? "—" : (
                                            <EditableNum
                                                step="0.01"
                                                value={ov.rate ?? l.rate}
                                                placeholder={String(l.rate)}
                                                onChange={(v) => setOverride(l.role, "rate", v)}
                                                testId={`labour-rate-${l.role}`}
                                            />
                                        )}
                                    </td>
                                    <td className="numeric font-semibold" data-label="Subtotal">
                                        {lineIsAuto ? (
                                            <EditableNum
                                                step="0.01"
                                                value={ov.subtotal ?? l.subtotal}
                                                placeholder={String(l.subtotal)}
                                                onChange={(v) => setOverride(l.role, "subtotal", v)}
                                                testId={`labour-subtotal-${l.role}`}
                                            />
                                        ) : formatCurrencyExact(l.subtotal)}
                                    </td>
                                    <td className="text-right">
                                        <button onClick={() => removeBaseLabour(l.role)} className="text-[var(--rom-danger)] hover:opacity-70 px-1" title="Remove this line" data-testid={`labour-del-${l.role}`}>×</button>
                                    </td>
                                </tr>
                            );
                        })}

                        {extras.map((x, idx) => (
                            <tr key={`x-${idx}`} className="bg-[var(--rom-orange-soft)]/40" data-testid={`labour-extra-${idx}`}>
                                <td className="card-title">
                                    <input
                                        className="input py-1 px-2 text-sm"
                                        value={x.role || ""}
                                        placeholder="Custom role name"
                                        onChange={(e) => setExtra(idx, "role", e.target.value)}
                                        data-testid={`labour-extra-role-${idx}`}
                                    />
                                </td>
                                <td className="numeric" data-label="Crew"><EditableNum value={x.headcount ?? 1} onChange={(v) => setExtra(idx, "headcount", v)} testId={`labour-extra-crew-${idx}`} /></td>
                                <td className="numeric" data-label="Hours"><EditableNum step="0.1" value={x.hours ?? 8} onChange={(v) => setExtra(idx, "hours", v)} testId={`labour-extra-hours-${idx}`} /></td>
                                <td className="numeric" data-label="Rate/hr"><EditableNum step="0.01" value={x.rate ?? 0} onChange={(v) => setExtra(idx, "rate", v)} testId={`labour-extra-rate-${idx}`} /></td>
                                <td className="numeric font-semibold" data-label="Subtotal">{formatCurrencyExact((Number(x.headcount) || 0) * (Number(x.hours) || 0) * (Number(x.rate) || 0))}</td>
                                <td className="text-right">
                                    <button onClick={() => delExtra(idx)} className="text-[var(--rom-danger)] hover:opacity-70" title="Remove row" data-testid={`labour-extra-del-${idx}`}>×</button>
                                </td>
                            </tr>
                        ))}

                        <tr>
                            <td colSpan={6} className="p-0">
                                <div className="flex">
                                    <button onClick={() => setLibraryFor("labour")} className="flex-1 py-2.5 text-xs font-semibold text-[var(--rom-navy)] hover:bg-[var(--rom-orange-soft)]/30 transition-colors border-r border-[var(--rom-border)]" data-testid="labour-add-from-library">
                                        + Add from Rate library
                                    </button>
                                    <button onClick={addExtra} className="flex-1 py-2.5 text-xs font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-text)] hover:bg-slate-50 transition-colors" data-testid="labour-add-row">
                                        + Add custom role row
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={4}>A: Labour total</td>
                            <td className="numeric font-display font-black" data-testid="labour-total">{formatCurrencyExact(result.labour_total)}</td>
                            <td></td>
                        </tr>
                    </tfoot>
                </table>
            </CollapsibleSection>

            <CollapsibleSection tag="B" summary={formatCurrency(result.consumables_total)} title="Consumables (PPE, plastic, tape, cleaners)">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
                    <Field label="% of Labour" hint="Enter any %, including negative (e.g. -5).">
                        <div className="flex items-center gap-2">
                            <div className="w-28">
                                <EditableNum
                                    step="0.5"
                                    value={inputs.consumablesPct == null || inputs.consumablesPct === "" ? "" : Number((inputs.consumablesPct * 100).toFixed(2))}
                                    placeholder="5"
                                    onChange={(v) => setConsumablesPct(v === "" || v == null ? "" : Number(v) / 100)}
                                    testId="b-consumables-pct"
                                />
                            </div>
                            <span className="text-sm text-[var(--rom-text-2)]">%</span>
                        </div>
                    </Field>
                    <KV k="Schedule A (Labour)" v={formatCurrencyExact(result.labour_total)} />
                    <KV k={consumablesChipLabel} v={formatCurrencyExact(result.consumables_total)} bold />
                </div>
            </CollapsibleSection>

            <CollapsibleSection tag="C" summary={formatCurrency(result.equipment_total)} title="Equipment plan (qty derived from sq ft × IICRC-style ratios)">
                <SectionToolbar dirty={eqDirty} onReset={resetEquip}>
                    All cells editable. Engine seeds Qty/Days from sq-ft ratios — override any value, or add custom equipment rows.
                </SectionToolbar>
                <table className="rom-table rom-table--responsive" data-testid="equipment-table">
                    <thead>
                        <tr>
                            <th>Item</th>
                            <th className="numeric">Qty</th>
                            <th className="numeric">Days</th>
                            <th className="numeric">Rate/day</th>
                            <th className="numeric">Subtotal (+10/10)</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {result.equipment_lines.filter((l) => !l.custom).map((l, i) => {
                            const ov = eqOverrides[l.item] || {};
                            return (
                                <tr key={`eq-${i}`} data-testid={`eq-row-${l.item}`}>
                                    <td className="font-medium card-title">
                                        {l.item}
                                        {l.overridden && <span className="ml-2 chip chip-orange" style={{padding: "1px 5px"}}>edited</span>}
                                        {l.assumption && !l.overridden && (
                                            <span className="ml-2 chip" style={{ padding: "1px 5px", background: "#fef3c7", color: "#92400e" }} title={`Assumption · unvalidated${l.basis ? ` · ${l.basis}` : ""}`} data-testid={`eq-assumption-${l.item}`}>
                                                assumption
                                            </span>
                                        )}
                                    </td>
                                    <td className="numeric" data-label="Qty">
                                        <EditableNum value={ov.qty ?? l.qty} placeholder={String(l.qty)} onChange={(v) => setEqOverride(l.item, "qty", v)} testId={`eq-qty-${l.item}`} />
                                    </td>
                                    <td className="numeric" data-label="Days">
                                        <EditableNum step="0.5" value={ov.days ?? l.days} placeholder={String(l.days)} onChange={(v) => setEqOverride(l.item, "days", v)} testId={`eq-days-${l.item}`} />
                                    </td>
                                    <td className="numeric" data-label="Rate/day">
                                        <EditableNum step="0.01" value={ov.rate ?? l.rate} placeholder={String(l.rate)} onChange={(v) => setEqOverride(l.item, "rate", v)} testId={`eq-rate-${l.item}`} />
                                    </td>
                                    <td className="numeric font-semibold" data-label="Subtotal">{formatCurrencyExact(l.subtotal)}</td>
                                    <td className="text-right">
                                        <button onClick={() => removeBaseEquip(l.item)} className="text-[var(--rom-danger)] hover:opacity-70 px-1" title="Remove this line" data-testid={`eq-del-${l.item}`}>×</button>
                                    </td>
                                </tr>
                            );
                        })}
                        {result.equipment_lines.filter((l) => !l.custom).length === 0 && eqExtras.length === 0 && (
                            <tr><td colSpan={6} className="text-center text-[var(--rom-text-3)] py-6">No equipment seeded for this loss type — add custom rows below.</td></tr>
                        )}

                        {eqExtras.map((x, idx) => (
                            <tr key={`eqx-${idx}`} className="bg-[var(--rom-orange-soft)]/40" data-testid={`eq-extra-${idx}`}>
                                <td className="card-title">
                                    <input className="input py-1 px-2 text-sm" value={x.item || ""} placeholder="Custom equipment" onChange={(e) => setEqExtra(idx, "item", e.target.value)} data-testid={`eq-extra-item-${idx}`} />
                                </td>
                                <td className="numeric" data-label="Qty"><EditableNum value={x.qty ?? 1} onChange={(v) => setEqExtra(idx, "qty", v)} testId={`eq-extra-qty-${idx}`} /></td>
                                <td className="numeric" data-label="Days"><EditableNum step="0.5" value={x.days ?? 1} onChange={(v) => setEqExtra(idx, "days", v)} testId={`eq-extra-days-${idx}`} /></td>
                                <td className="numeric" data-label="Rate/day"><EditableNum step="0.01" value={x.rate ?? 0} onChange={(v) => setEqExtra(idx, "rate", v)} testId={`eq-extra-rate-${idx}`} /></td>
                                <td className="numeric font-semibold" data-label="Subtotal">{formatCurrencyExact((Number(x.qty) || 0) * (Number(x.days) || 0) * (Number(x.rate) || 0) * (schedule?.conditions?.overhead_profit_markup || 1.2))}</td>
                                <td className="text-right">
                                    <button onClick={() => delEqExtra(idx)} className="text-[var(--rom-danger)] hover:opacity-70" data-testid={`eq-extra-del-${idx}`}>×</button>
                                </td>
                            </tr>
                        ))}

                        <tr>
                            <td colSpan={6} className="p-0">
                                <div className="flex">
                                    <button onClick={() => setLibraryFor("equipment")} className="flex-1 py-2.5 text-xs font-semibold text-[var(--rom-navy)] hover:bg-[var(--rom-orange-soft)]/30 transition-colors border-r border-[var(--rom-border)]" data-testid="eq-add-from-library">
                                        + Add from Rate library
                                    </button>
                                    <button onClick={addEqExtra} className="flex-1 py-2.5 text-xs font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-text)] hover:bg-slate-50 transition-colors" data-testid="eq-add-row">
                                        + Add custom equipment row
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={4}>C: Equipment total</td>
                            <td className="numeric font-display font-black" data-testid="equipment-total">{formatCurrencyExact(result.equipment_total)}</td>
                            <td></td>
                        </tr>
                    </tfoot>
                </table>
            </CollapsibleSection>

            <CollapsibleSection tag="D" summary={formatCurrency(result.subs_total || 0)} title="Subcontractors (categorised — trade, labour, or rental equipment via sub)">
                <SectionToolbar dirty={false} onReset={() => {}}>
                    Default markup 20% per line. Tag <b>Rental Equipment via Sub</b> so equipment costs booked through a sub stay visible (not buried in a flat sub guess).
                </SectionToolbar>
                <table className="rom-table rom-table--responsive" data-testid="subs-table">
                    <thead>
                        <tr>
                            <th>Item / Service</th>
                            <th>Category</th>
                            <th className="numeric">Qty</th>
                            <th className="numeric">Rate</th>
                            <th className="numeric">Days</th>
                            <th className="numeric">Markup %</th>
                            <th className="numeric">Subtotal</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {subs.length === 0 && (
                            <tr><td colSpan={8} className="text-center text-[var(--rom-text-3)] py-4">No subcontractors yet.</td></tr>
                        )}
                        {subs.map((s, idx) => {
                            const line = result.subcontractor_lines[idx] || { subtotal: 0 };
                            return (
                                <tr key={`sub-${idx}`} data-testid={`sub-row-${idx}`}>
                                    <td className="card-title">
                                        <input className="input py-1 px-2 text-sm" value={s.item || ""} placeholder="e.g. Fire watch" onChange={(e) => setSub(idx, "item", e.target.value)} data-testid={`sub-item-${idx}`} />
                                    </td>
                                    <td data-label="Category">
                                        <select className="select py-1 px-2 text-sm" value={s.category || "trade"} onChange={(e) => setSub(idx, "category", e.target.value)} data-testid={`sub-category-${idx}`}>
                                            {SUB_CATEGORIES.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                                        </select>
                                    </td>
                                    <td className="numeric" data-label="Qty"><EditableNum value={s.qty ?? 1} onChange={(v) => setSub(idx, "qty", v)} testId={`sub-qty-${idx}`} /></td>
                                    <td className="numeric" data-label="Rate"><EditableNum step="0.01" value={s.rate ?? 0} onChange={(v) => setSub(idx, "rate", v)} testId={`sub-rate-${idx}`} /></td>
                                    <td className="numeric" data-label="Days"><EditableNum step="0.5" value={s.days ?? 1} onChange={(v) => setSub(idx, "days", v)} testId={`sub-days-${idx}`} /></td>
                                    <td className="numeric" data-label="Markup %">
                                        <EditableNum step="0.01" value={((s.markupPct ?? 0.20) * 100).toFixed(2)} onChange={(v) => setSub(idx, "markupPct", Number(v) / 100)} testId={`sub-markup-${idx}`} />
                                    </td>
                                    <td className="numeric font-semibold" data-label="Subtotal">{formatCurrencyExact(line.subtotal)}</td>
                                    <td className="text-right">
                                        <button onClick={() => delSub(idx)} className="text-[var(--rom-danger)] hover:opacity-70" data-testid={`sub-del-${idx}`}>×</button>
                                    </td>
                                </tr>
                            );
                        })}
                        <tr>
                            <td colSpan={8} className="p-0">
                                <div className="flex divide-x divide-[var(--rom-border)] border-t border-[var(--rom-border)]">
                                    <button onClick={() => setLibraryFor("subs")} className="flex-1 py-2.5 text-xs font-semibold text-[var(--rom-navy)] hover:bg-[var(--rom-orange-soft)]/30 transition-colors" data-testid="sub-add-from-library">
                                        + Add from Vendor roster
                                    </button>
                                    <button onClick={addSub} className="flex-1 py-2.5 text-xs font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-text)] hover:bg-slate-50 transition-colors" data-testid="sub-add-row">
                                        + Add blank row
                                    </button>
                                </div>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td colSpan={6}>D: Subcontractors total</td>
                            <td className="numeric font-display font-black" data-testid="subs-total">{formatCurrencyExact(result.subs_total || 0)}</td>
                            <td></td>
                        </tr>
                    </tfoot>
                </table>
            </CollapsibleSection>

            <CollapsibleSection tag="E" summary={formatCurrency(result.incidentals_total)} title="Miscellaneous / Incidentals (per-diem, mobilisation, permits, disposal, travel)">
                <SectionToolbar dirty={miscDirty} onReset={resetMisc}>
                    Engine-suggested values shown as placeholders — edit any cell to override. Add custom line items at the bottom.
                </SectionToolbar>
                <table className="rom-table rom-table--responsive" data-testid="misc-table">
                    <thead>
                        <tr>
                            <th>Line item</th>
                            <th className="numeric">Value</th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        <MiscRow label="Per diem" engineVal={bi.per_diem_total} ovKey="per_diem" miscOv={miscOv} setMiscOv={setMiscOv} hint={`$60 × ${result.crew_size} × ${result.days}d`} assumption />
                        <MiscRow label="Mobilisation" engineVal={bi.mobilisation} ovKey="mobilisation" miscOv={miscOv} setMiscOv={setMiscOv} hint={bi.distance_miles ? `${bi.distance_miles} mi · tiered` : "tiered by distance"} assumption />
                        <MiscRow label="Permits" engineVal={bi.permits} ovKey="permits" miscOv={miscOv} setMiscOv={setMiscOv} hint={(inputs.lossType === "fire" || inputs.includeRecon) ? "Triggered" : "Not triggered"} />
                        <MiscRow label="Disposal" engineVal={bi.disposal} ovKey="disposal" miscOv={miscOv} setMiscOv={setMiscOv} hint="tiered $/sqft" assumption />
                        {miscExtras.map((x, idx) => (
                            <tr key={`me-${idx}`} className="bg-[var(--rom-orange-soft)]/40" data-testid={`misc-extra-${idx}`}>
                                <td className="card-title">
                                    <input className="input py-1 px-2 text-sm" value={x.label || ""} placeholder="Custom misc item" onChange={(e) => setMiscExtra(idx, "label", e.target.value)} data-testid={`misc-extra-label-${idx}`} />
                                </td>
                                <td className="numeric" data-label="Value">
                                    <div className="flex items-center justify-end gap-1">
                                        <EditableNum value={x.qty ?? 1} onChange={(v) => setMiscExtra(idx, "qty", v)} testId={`misc-extra-qty-${idx}`} />
                                        <span className="text-[10px] text-[var(--rom-text-3)]">×</span>
                                        <EditableNum step="0.01" value={x.rate ?? 0} onChange={(v) => setMiscExtra(idx, "rate", v)} testId={`misc-extra-rate-${idx}`} />
                                        <span className="text-[10px] text-[var(--rom-text-3)]">×</span>
                                        <EditableNum step="0.5" value={x.days ?? 1} onChange={(v) => setMiscExtra(idx, "days", v)} testId={`misc-extra-days-${idx}`} />
                                        <span className="text-xs font-mono ml-2 font-semibold">= {formatCurrencyExact((Number(x.qty) || 0) * (Number(x.rate) || 0) * ((x.days != null ? Number(x.days) : 1) || 1))}</span>
                                    </div>
                                </td>
                                <td className="text-right">
                                    <button onClick={() => delMiscExtra(idx)} className="text-[var(--rom-danger)] hover:opacity-70" data-testid={`misc-extra-del-${idx}`}>×</button>
                                </td>
                            </tr>
                        ))}
                        <tr>
                            <td colSpan={3} className="p-0">
                                <button onClick={addMiscExtra} className="w-full py-2.5 text-xs font-semibold text-[var(--rom-text-2)] hover:text-[var(--rom-text)] hover:bg-slate-50 transition-colors" data-testid="misc-add-row">
                                    + Add custom misc line
                                </button>
                            </td>
                        </tr>
                    </tbody>
                    <tfoot>
                        <tr>
                            <td>E: Misc / Incidentals total (+10/10)</td>
                            <td className="numeric font-display font-black" data-testid="incidentals-total">{formatCurrencyExact(result.incidentals_total)}</td>
                            <td></td>
                        </tr>
                    </tfoot>
                </table>
            </CollapsibleSection>

            <CollapsibleSection tag="F" summary={inputs.projectStartDate ? `${fmtDateShort(inputs.projectStartDate)} → ${fmtDateShort(addDaysISO(inputs.projectStartDate, result.days - 1))} · ${result.days}d` : `${result.days} days`} title="Project plan (suggested phases, dates & status)">
                <GanttTimeline
                    phases={result.timeline}
                    totalDays={result.days}
                    startDate={inputs.projectStartDate}
                    onStartDateChange={(v) => setField("projectStartDate", v)}
                    onPhaseChange={onPhaseChange}
                    onPhaseExtraChange={onPhaseExtraChange}
                    onAddPhase={onAddPhase}
                    onReset={resetTimeline}
                    hasOverrides={timelineDirty}
                />
            </CollapsibleSection>

            <RateLibraryPicker
                open={!!libraryFor}
                onClose={() => setLibraryFor(null)}
                title={libraryFor === "labour" ? "Schedule A — Labour library"
                    : libraryFor === "subs" ? "Schedule D — Subcontractor / vendor roster"
                    : "Schedule C — Equipment library"}
                subtitle={libraryFor === "labour"
                    ? `Pick a role from the active rate schedule. ${usePrime ? "Prime" : "Standard"} rates shown. Click an item already in the table to bump its crew by 1.`
                    : libraryFor === "subs"
                    ? "Pick a vendor/sub from the managed roster. Category & default markup carry over. Click one already added to bump its quantity by 1."
                    : "Pick an item from the active equipment catalogue. Click an item already in the table to bump its quantity by 1."}
                items={pickerItems}
                onPick={onPickLibrary}
            />
        </>
    );
}
